import { getFabricWindow } from 'fabric';
import type { LoadImageOptions } from '../util/misc/objectEnlive';
import { parseSVGDocument } from './parseSVGDocument';
import type { SVGParsingOutput, TSvgReviverCallback } from './typedefs';

const initializeCanvas = (fontFamily: string, fontSize: number, fontWeight: string, fontStyle: string): CanvasRenderingContext2D => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = `${fontWeight} ${fontStyle} ${fontSize}px ${fontFamily}`;
    return context;
};

// Function to measure text width with caching
const measureTextWidthWithCache = (context: CanvasRenderingContext2D, text: string, cache: Map<string, number>, fontKey: string): number => {
    const cacheKey = `${fontKey}-${text}`;
    if (cache.has(cacheKey)) {
        return cache.get(cacheKey);
    } else {
        const width = context.measureText(text).width;
        cache.set(cacheKey, width);
        return width;
    }
};

const detectAndSplitHiddenSpaces = (svgData: string): string => {
    const cache = new Map();
    const parser = new DOMParser();
    const svgDocument = parser.parseFromString(svgData, 'image/svg+xml');
    const tspanElements = svgDocument.querySelectorAll('tspan');

    tspanElements.forEach(tspan => {
        const xValues = tspan.getAttribute('x')?.split(' ').map(Number) || [];
        const textContent = tspan.textContent || '';

        let previousX = xValues[0];
        const newTspans: HTMLElement[] = [];

        // Extract font properties from the <text> or <tspan> attributes
        const fontFamily = tspan.closest('text')?.getAttribute('font-family') || 'Arial';
        const fontSize = parseFloat(tspan.closest('text')?.getAttribute('font-size') || '14');
        const fontWeight = tspan.closest('text')?.getAttribute('font-weight') || 'normal';
        const fontStyle = tspan.closest('text')?.getAttribute('font-style') || 'normal';

        // Create a unique key for caching based on the font properties
        const fontKey = `${fontFamily}-${fontWeight}-${fontStyle}-${fontSize}`;

        // Initialize the canvas context with the font properties
        const context = initializeCanvas(fontFamily, fontSize, fontWeight, fontStyle);

        let startIdx = 0;

        for (let i = 1; i < xValues.length; i++) {
            const currentX = xValues[i];
            const char = textContent[i - 1];

            // Measure the width of the character and space with caching
            const charWidth = measureTextWidthWithCache(context, char, cache, fontKey);
            const spaceWidth = measureTextWidthWithCache(context, ' ', cache, fontKey);

            if ((currentX - previousX) > (charWidth + spaceWidth - 0.8)) {
                // Split the text content into two parts
                const newTextContent = textContent.slice(startIdx, i);

                // Create a new <tspan> for the text up to the detected space
                const newTspan = tspan.cloneNode() as HTMLElement;
                newTspan.textContent = newTextContent;

                // Adjust the x attribute for the new <tspan>
                newTspan.setAttribute('x', xValues[startIdx].toString());

                // Add the new tspan to the list
                newTspans.push(newTspan);

                // Update startIdx to the current position
                startIdx = i;
            }

            previousX = currentX;
        }

        // Add the remaining text after the last split
        if (startIdx < textContent.length) {
            const newTspan = tspan.cloneNode() as HTMLElement;
            newTspan.textContent = textContent.slice(startIdx);
            newTspan.setAttribute('x', xValues[startIdx].toString());
            newTspans.push(newTspan);
        }

        // Replace the original <tspan> with the new ones
        const docFragment = document.createDocumentFragment();
        newTspans.forEach(newTspan => {
            docFragment.appendChild(newTspan);
        });
        tspan.parentNode?.replaceChild(docFragment, tspan);
    });

    // Return the modified SVG data as a string
    return new XMLSerializer().serializeToString(svgDocument);
};

const mergeTspans = (svgData: string): string => {
    const cache = new Map();
    const parser = new DOMParser();
    const svgDocument = parser.parseFromString(svgData, 'image/svg+xml');
    const tspanElements = Array.from(svgDocument.querySelectorAll('tspan'));

    let i = 0;
    while (i < tspanElements.length - 1) {
        const currentTspan = tspanElements[i];
        const nextTspan = tspanElements[i + 1];

        if (isIconOrGlyph(currentTspan.textContent) || isIconOrGlyph(nextTspan.textContent)) {
            i++;
            continue; // Skip merging if either tspan contains an icon, glyph, or special character
        }

        const currentXValues = currentTspan.getAttribute('x')?.split(' ').map(Number) || [];
        const nextXValues = nextTspan.getAttribute('x')?.split(' ').map(Number) || [];

        const currentText = currentTspan.textContent || '';
        const nextText = nextTspan.textContent || '';

        const currentLastX = currentXValues[currentXValues.length - 1];
        const nextFirstX = nextXValues[0];

        const currentTextElement = currentTspan.closest('text');
        const nextTextElement = nextTspan.closest('text');

        if (!currentTextElement || !nextTextElement) {
            i++;
            continue;
        }

        const currentFontFamily = currentTextElement.getAttribute('font-family') || 'Arial';
        const currentFontSize = parseFloat(currentTextElement.getAttribute('font-size') || '14');
        const currentFontWeight = currentTextElement.getAttribute('font-weight') || 'normal';
        const currentFontStyle = currentTextElement.getAttribute('font-style') || 'normal';
        const currentY = currentTspan.getAttribute('y') || '';
        const currentTransform = currentTextElement.getAttribute('transform') || '';

        const nextFontFamily = nextTextElement.getAttribute('font-family') || 'Arial';
        const nextFontSize = parseFloat(nextTextElement.getAttribute('font-size') || '14');
        const nextFontWeight = nextTextElement.getAttribute('font-weight') || 'normal';
        const nextFontStyle = nextTextElement.getAttribute('font-style') || 'normal';
        const nextY = nextTspan.getAttribute('y') || '';
        const nextTransform = nextTextElement.getAttribute('transform') || '';

        if (
            currentFontFamily === nextFontFamily &&
            currentFontSize === nextFontSize &&
            currentFontWeight === nextFontWeight &&
            currentFontStyle === nextFontStyle &&
            currentY === nextY &&
            currentTransform === nextTransform
        ) {
            const fontKey = `${currentFontFamily}-${currentFontWeight}-${currentFontStyle}-${currentFontSize}`;
            const context = initializeCanvas(currentFontFamily, currentFontSize, currentFontWeight, currentFontStyle);

            const lastChar = currentText[currentText.length - 1];
            const spaceWidth = measureTextWidthWithCache(context, ' ', cache, fontKey);
            const lastCharWidth = measureTextWidthWithCache(context, lastChar, cache, fontKey);

            if ((nextFirstX - currentLastX) < (lastCharWidth + spaceWidth - 1)) {
                currentTspan.textContent = currentText + nextText;
                currentTspan.setAttribute('x', [...currentXValues, ...nextXValues].join(' '));
                nextTspan.parentNode?.removeChild(nextTspan);
                tspanElements.splice(i + 1, 1); // Remove the merged next tspan from the list
                // Don't increment i, so we can check the current tspan against the next one after merging
            } else {
                i++; // Move to the next tspan
            }
        } else {
            i++; // Move to the next tspan if fonts or other properties do not match
        }
    }

    return new XMLSerializer().serializeToString(svgDocument);
};

const isIconOrGlyph = (text: string): boolean => {
    const specialCharacters = new Set(['', '•', '⚙', '✪']); // Add more characters as needed

    for (const char of text) {
        const codePoint = char.codePointAt(0);
        if (specialCharacters.has(char)) {
            return true; // Character is a specific special character
        }
        // Check if character is within common icon/glyph ranges
        const iconGlyphRanges = [
            [0x2600, 0x26FF], // Miscellaneous Symbols
            [0x1F300, 0x1F5FF], // Miscellaneous Symbols and Pictographs
            [0x1F600, 0x1F64F], // Emoticons
            [0x1F680, 0x1F6FF], // Transport and Map Symbols
            [0x1F700, 0x1F77F], // Alchemical Symbols
            [0x1F780, 0x1F7FF], // Geometric Shapes Extended
            [0x1F800, 0x1F8FF], // Supplemental Arrows-C
            [0x1F900, 0x1F9FF], // Supplemental Symbols and Pictographs
            [0x1FA00, 0x1FA6F], // Chess Symbols
            [0x1FA70, 0x1FAFF], // Symbols and Pictographs Extended-A
        ];

        for (const [start, end] of iconGlyphRanges) {
            if (codePoint >= start && codePoint <= end) {
                return true; // Character is likely an icon or glyph
            }
        }
    }
    return false; // Text does not contain icons, glyphs, or specific special characters
};

export function loadSVGFromString(
    string: string,
    reviver?: TSvgReviverCallback,
    options?: LoadImageOptions
): Promise<SVGParsingOutput> {
    const mergeSVGString = mergeTspans(string);
    const modifiedSVGString = detectAndSplitHiddenSpaces(mergeSVGString);
    const parser = new (getFabricWindow().DOMParser)(),
        doc = parser.parseFromString(modifiedSVGString.trim(), 'text/xml');

    // Clone tspan elements into separate text nodes
    cloneTspansIntoTextNodes(doc);
    return parseSVGDocument(doc, reviver, options);
}

function cloneTspansIntoTextNodes(doc: Document) {
    const texts = doc.getElementsByTagName('text');
    const docFragment = doc.createDocumentFragment();

    for (let i = texts.length - 1; i >= 0; i--) {
        const textElement = texts[i];
        const tspans = textElement.getElementsByTagName('tspan');

        if (tspans.length > 1) {
            for (let j = 0; j < tspans.length; j++) {
                const tspan = tspans[j];
                const newText = doc.createElementNS('http://www.w3.org/2000/svg', 'text');

                // Copy attributes from the original text element
                for (let attr of textElement.attributes) {
                    newText.setAttribute(attr.name, attr.value);
                }

                newText.appendChild(tspan.cloneNode(true));

                docFragment.appendChild(newText);
            }

            textElement.parentNode?.replaceChild(docFragment, textElement);
        }
    }
}
